import React from 'react'
import PropTypes from 'prop-types'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import WarningIcon from '@mui/icons-material/Warning'
import { SaveRounded, TextSnippetOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import ScopesSection from './ScopesSection'
import SmartSelect from '../../../../generic/smartSelect/SmartSelect'
import SmartTextField from '../../../../generic/smarttextfield/SmartTextField'

const noop = () => {}

const QualityIncidenceCreateForm = ({
  t_,
  theme,
  format,
  onSubmit,
  onClose,
  isSaving,
  isFormLocked,
  showRequiredErrors,
  incidenceForm,
  statusSelectOptions,
  statusLabelByValue,
  prioritySelectOptions,
  priorityLabelByValue,
  isLoadingWorkers,
  isLoadingWorkflowResources,
  fabricationOrderDisplayName,
  fabricationOrderSelectOptions,
  handleFabricationOrderChange,
  isFabricationOrderSelectable,
  isFabricationOrderValid,
  jobDisplayName,
  jobSelectOptions,
  isJobSelectable,
  deviceDisplayName,
  deviceSelectOptions,
  handleDeviceChange,
  isDeviceSelectable,
  isLoadingDevices,
  workerDisplayName,
  workerSelectOptions,
  handleWorkerChange,
  isWorkerSelectable,
  isDescriptionValid,
  descriptionErrorMessage,
  handleTitleChange,
  handleCodeChange,
  handleStatusChange,
  handlePriorityChange,
  handleCreatedDateChange,
  handleJobChange,
  handleResponsibleChange,
  handleDescriptionInputChange,
  handlePreliminaryAnalysisInputChange,
  handleProposedSolutionInputChange,
  handleScopeRowsChange,
  handleScopesEndDateChange,
  handleActionsChange,
  checkIfIsNotEmpty,
  checkCodeError,
  isStatusValid,
  isPriorityValid,
  AlertElement,
  openDocumentsDialog,
  isDocumentsDisabled
}) => {
  return (
    <Box component='form' onSubmit={onSubmit} noValidate sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {AlertElement}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {t_('Quality incidence')}
          </Typography>
          <Stack direction='row' justifyContent='right' spacing={1}>
            <Button
              type='button'
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
              onClick={openDocumentsDialog}
              disabled={isDocumentsDisabled}
            >
              <TextSnippetOutlined sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                {t_('Documents').toUpperCase()}
              </Typography>
            </Button>
            <Button
              type='button'
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
              onClick={onClose}
              disabled={isSaving}
            >
              <CloseRoundedIcon sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                {t_('Close').toUpperCase()}
              </Typography>
            </Button>
            <Button
              type='submit'
              disabled={isSaving || isFormLocked}
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
            >
              {isSaving
                ? <CircularProgress size={18} color='inherit' />
                : <SaveRounded sx={{ fontSize: '1.2em' }} />}
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                {isSaving ? t_('Saving').toUpperCase() : t_('Save').toUpperCase()}
              </Typography>
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} alignItems='stretch'>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <SmartTextField
              label={t_('Title')}
              value={incidenceForm.title}
              onChange={handleTitleChange}
              checkError={showRequiredErrors ? (v) => checkIfIsNotEmpty(v, 'Title') : undefined}
              forceCheckError={showRequiredErrors}
              required
              disabled={isFormLocked}
              style={{ width: '100%' }}
              inputProps={{ maxLength: 120 }}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <SmartTextField
              label={t_('Code')}
              value={incidenceForm.code}
              onChange={handleCodeChange}
              checkError={checkCodeError}
              disabled
              style={{ width: '100%' }}
              inputProps={{ maxLength: 18 }}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <SmartSelect
              label={t_('Status') + ' *'}
              value={statusLabelByValue.get(incidenceForm.status) || ''}
              selectableOptions={statusSelectOptions.map((opt) => opt.label)}
              onChange={handleStatusChange}
              disabled={isFormLocked}
              error={showRequiredErrors && !isStatusValid}
              size='medium'
              width='100%'
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <SmartSelect
              label={t_('Priority') + ' *'}
              value={priorityLabelByValue.get(incidenceForm.priority) || ''}
              selectableOptions={prioritySelectOptions.map((opt) => opt.label)}
              onChange={handlePriorityChange}
              disabled={isFormLocked}
              error={showRequiredErrors && !isPriorityValid}
              size='medium'
              width='100%'
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <DateTimePicker
              label={t_('Created date')}
              slotProps={{ textField: { InputLabelProps: { shrink: true }, fullWidth: true } }}
              format={format.dateTime}
              ampm={false}
              value={incidenceForm.createdDate}
              onChange={handleCreatedDateChange}
              disabled={isFormLocked}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            {isFabricationOrderSelectable
              ? (
                <SmartSelect
                  label={t_('Fabrication order (OF)')}
                  value={fabricationOrderDisplayName}
                  selectableOptions={fabricationOrderSelectOptions}
                  onChange={handleFabricationOrderChange}
                  disabled={isFormLocked}
                  error={false}
                  disableClearable={false}
                  width='100%'
                />
                )
              : (
                <SmartTextField
                  label={t_('Fabrication order (OF)')}
                  value={fabricationOrderDisplayName}
                  onChange={noop}
                  disabled
                  style={{ width: '100%' }}
                />
                )}
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            {isJobSelectable
              ? (
                <SmartSelect
                  label={t_('Job')}
                  value={incidenceForm.jobLabel}
                  selectableOptions={jobSelectOptions}
                  onChange={handleJobChange}
                  disabled={isFormLocked || !fabricationOrderDisplayName}
                  disableClearable={false}
                  autoSelectFirst={false}
                  width='100%'
                />
                )
              : (
                <SmartTextField
                  label={t_('Job')}
                  value={jobDisplayName}
                  onChange={noop}
                  disabled
                  style={{ width: '100%' }}
                />
                )}
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            {isDeviceSelectable
              ? (
                <SmartSelect
                  label={t_('Device')}
                  value={deviceDisplayName}
                  selectableOptions={deviceSelectOptions}
                  onChange={handleDeviceChange}
                  disabled={isFormLocked || isLoadingDevices}
                  helperText={isLoadingDevices ? t_('Loading') : ''}
                  disableClearable={false}
                  width='100%'
                />
                )
              : (
                <SmartTextField
                  label={t_('Device')}
                  value={deviceDisplayName}
                  onChange={noop}
                  disabled
                  style={{ width: '100%' }}
                />
                )}
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            {isWorkerSelectable
              ? (
                <SmartSelect
                  label={t_('Worker')}
                  value={workerDisplayName}
                  selectableOptions={workerSelectOptions}
                  onChange={handleWorkerChange}
                  disabled={isLoadingWorkers || isFormLocked}
                  helperText={isLoadingWorkers ? t_('Loading') : ''}
                  disableClearable={false}
                  width='100%'
                />
                )
              : (
                <SmartTextField
                  label={t_('Worker')}
                  value={workerDisplayName}
                  onChange={noop}
                  disabled
                  style={{ width: '100%' }}
                />
                )}
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <SmartTextField
              label={t_('Responsible')}
              value={incidenceForm?.responsibleName || ''}
              onChange={handleResponsibleChange}
              disabled={isLoadingWorkers || isFormLocked}
              helperText={isLoadingWorkers ? t_('Loading') : ''}
              style={{ width: '100%' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: -0.5 }}>
              {t_('Leave job empty to apply to entire fabrication order (OF)')}
            </Typography>
          </Grid>

          <Grid item xs={12} textAlign='left' style={{ paddingTop: 8 }}>
            <Box
              component='fieldset'
              sx={{
                borderColor: showRequiredErrors && !isDescriptionValid ? theme.palette.error.main : '#ffffff4d',
                borderRadius: 1,
                padding: '4px 14px 14px',
                minWidth: 0,
                '&:focus-within': {
                  borderColor: showRequiredErrors && !isDescriptionValid ? theme.palette.error.main : theme.palette.primary.main
                }
              }}
            >
              <Box
                component='legend'
                sx={{
                  px: 1,
                  fontSize: '0.75rem',
                  color: showRequiredErrors && !isDescriptionValid ? theme.palette.error.main : undefined
                }}
              >
                {showRequiredErrors && !isDescriptionValid
                  ? (
                    <Tooltip placement='top' title={descriptionErrorMessage} style={{ fontSize: '1.2rem' }}>
                      <WarningIcon />
                    </Tooltip>
                    )
                  : t_('Description') + ' *'}
              </Box>
              <Box
                component='textarea'
                sx={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  font: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  minHeight: '7rem'
                }}
                onChange={handleDescriptionInputChange}
                value={incidenceForm.description}
                aria-label={t_('Description')}
                disabled={isFormLocked}
              />
            </Box>
          </Grid>
          <Grid item xs={12} textAlign='left' style={{ paddingTop: 8 }}>
            <Box
              component='fieldset'
              sx={{
                borderColor: '#ffffff4d',
                borderRadius: 1,
                padding: '4px 14px 14px',
                minWidth: 0,
                '&:focus-within': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <Box
                component='legend'
                sx={{
                  px: 1,
                  fontSize: '0.75rem'
                }}
              >
                {t_('Preliminary analysis')}
              </Box>
              <Box
                component='textarea'
                sx={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  font: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  minHeight: '6rem'
                }}
                onChange={handlePreliminaryAnalysisInputChange}
                value={incidenceForm.preliminaryAnalysis}
                aria-label={t_('Preliminary analysis')}
                disabled={isFormLocked}
              />
            </Box>
          </Grid>
          <Grid item xs={12} textAlign='left' style={{ paddingTop: 8 }}>
            <Box
              component='fieldset'
              sx={{
                borderColor: '#ffffff4d',
                borderRadius: 1,
                padding: '4px 14px 14px',
                minWidth: 0,
                '&:focus-within': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <Box
                component='legend'
                sx={{
                  px: 1,
                  fontSize: '0.75rem'
                }}
              >
                {t_('Proposed solution')}
              </Box>
              <Box
                component='textarea'
                sx={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  font: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  minHeight: '6rem'
                }}
                onChange={handleProposedSolutionInputChange}
                value={incidenceForm.proposedSolution}
                aria-label={t_('Proposed solution')}
                disabled={isFormLocked}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            {isLoadingWorkflowResources && (
              <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 1 }}>
                <CircularProgress size={18} />
                <Typography variant='body2' color='text.secondary'>
                  {t_('Loading materials')}
                </Typography>
              </Stack>
            )}

            <ScopesSection
              t_={t_}
              format={format}
              scopeRows={incidenceForm.scopeRows}
              onScopeRowsChange={handleScopeRowsChange}
              endDate={incidenceForm.scopesEndDate}
              onEndDateChange={handleScopesEndDateChange}
              disabled={isFormLocked}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

QualityIncidenceCreateForm.propTypes = {
  t_: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  format: PropTypes.shape({ dateTime: PropTypes.string }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isFormLocked: PropTypes.bool.isRequired,
  showRequiredErrors: PropTypes.bool.isRequired,
  incidenceForm: PropTypes.object.isRequired,
  statusSelectOptions: PropTypes.array.isRequired,
  statusLabelByValue: PropTypes.instanceOf(Map).isRequired,
  prioritySelectOptions: PropTypes.array.isRequired,
  priorityLabelByValue: PropTypes.instanceOf(Map).isRequired,
  isLoadingWorkers: PropTypes.bool.isRequired,
  isLoadingWorkflowResources: PropTypes.bool.isRequired,
  fabricationOrderDisplayName: PropTypes.string.isRequired,
  fabricationOrderSelectOptions: PropTypes.array,
  handleFabricationOrderChange: PropTypes.func,
  isFabricationOrderSelectable: PropTypes.bool,
  isFabricationOrderValid: PropTypes.bool,
  jobDisplayName: PropTypes.string.isRequired,
  jobSelectOptions: PropTypes.array,
  isJobSelectable: PropTypes.bool,
  deviceDisplayName: PropTypes.string.isRequired,
  deviceSelectOptions: PropTypes.array,
  handleDeviceChange: PropTypes.func,
  isDeviceSelectable: PropTypes.bool,
  isLoadingDevices: PropTypes.bool,
  workerDisplayName: PropTypes.string.isRequired,
  workerSelectOptions: PropTypes.array,
  handleWorkerChange: PropTypes.func,
  isWorkerSelectable: PropTypes.bool,
  isDescriptionValid: PropTypes.bool.isRequired,
  descriptionErrorMessage: PropTypes.string.isRequired,
  handleTitleChange: PropTypes.func.isRequired,
  handleCodeChange: PropTypes.func.isRequired,
  handleStatusChange: PropTypes.func.isRequired,
  handlePriorityChange: PropTypes.func.isRequired,
  handleCreatedDateChange: PropTypes.func.isRequired,
  handleJobChange: PropTypes.func.isRequired,
  handleResponsibleChange: PropTypes.func.isRequired,
  handleDescriptionInputChange: PropTypes.func.isRequired,
  handlePreliminaryAnalysisInputChange: PropTypes.func.isRequired,
  handleProposedSolutionInputChange: PropTypes.func.isRequired,
  handleScopeRowsChange: PropTypes.func.isRequired,
  handleScopesEndDateChange: PropTypes.func.isRequired,
  handleActionsChange: PropTypes.func.isRequired,
  checkIfIsNotEmpty: PropTypes.func.isRequired,
  checkCodeError: PropTypes.func.isRequired,
  isStatusValid: PropTypes.bool.isRequired,
  isPriorityValid: PropTypes.bool.isRequired,
  AlertElement: PropTypes.node,
  openDocumentsDialog: PropTypes.func.isRequired,
  isDocumentsDisabled: PropTypes.bool.isRequired
}

QualityIncidenceCreateForm.defaultProps = {
  fabricationOrderSelectOptions: [],
  handleFabricationOrderChange: noop,
  isFabricationOrderSelectable: false,
  isFabricationOrderValid: true,
  jobSelectOptions: [],
  isJobSelectable: false,
  deviceSelectOptions: [],
  handleDeviceChange: noop,
  isDeviceSelectable: false,
  isLoadingDevices: false,
  workerSelectOptions: [],
  handleWorkerChange: noop,
  isWorkerSelectable: false
}

export default React.memo(QualityIncidenceCreateForm)
